import React, { useState } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import IndexApp from "../../../Components/IndexApp";
import { ClientDetail } from "../../../Repositories/ClientDetailRepository";
import { AccountStatusLabel } from "../../../Enum/AccountStatus";
import { capitalizeFirstLetter, formatPhoneNumber } from "../../../Utils/Function";
import { getActiveClientBrandConfig, getActivePrestationBrandConfig, isDepositEnabled } from "../../../config/environment.config";
import { isVerifiedTrainer } from "../../../Utils/VerifiedTrainerAmounts";
import { DV2 } from "../../DeclarationV2/theme";
import { euros, hoursFromHalfHours, monthName, plural } from "../../DeclarationV2/format";
import { vocab } from "../../DeclarationV2/vocabulary";
import { Avatar, Badge, Card, KeyValue, Notice, TextLink, useDv2Theme } from "../../DeclarationV2/layout/Ui";
import ClientRowActions from "../ClientRowActions";
import { ClientStatusBanner } from "../ClientStatusBanner";
import { ClientDetailLocationState, getClientListPath, isRegistrationFinalized } from "../clientDetailNavigation";
import useClientDetail from "../ClientDetail/useClientDetail";
import { DetailSectionStyleContext } from "../ClientDetail/DetailSection";
import { formatDate, formatUrssafDescription } from "../ClientDetail/clientDetailFormat";
import ClientTravelSection from "../ClientDetail/ClientTravelSection";
import ClientMonthSessionsSection from "../ClientDetail/ClientMonthSessionsSection";
import DocumentSpace from "../../../Components/App/Documents/DocumentSpace";

/**
 * Fiche élève, page repensée (maquette 4a du 30 octobre 2026), route
 * `/{marque}/tableau-de-bord/{eleve|client}/:clientId`. Mêmes rubriques que
 * la fiche actuelle (identité, activité, avance immédiate, tarifs, séances,
 * déplacement, acomptes, documents), mêmes appels (`useClientDetail`) et
 * mêmes actions (`ClientRowActions`), en deux colonnes : à gauche qui est
 * l'élève et où il en est, à droite ce qui s'est passé et ce qui arrive. Les
 * compteurs à zéro sont relégués en texte. L'ancienne page est conservée.
 */

const SectionCard = ({ title, subtitle, action, children }: { title: string; subtitle?: React.ReactNode; action?: React.ReactNode; children: React.ReactNode }) => (
    <Card style={{ display: "flex", flexDirection: "column", gap: 12, padding: 22 }} as="section" aria-label={title}>
        <div className="flex items-start justify-between gap-3">
            <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 15 }}>{title}</div>
                {subtitle && <div style={{ fontSize: 13, color: DV2.muted }}>{subtitle}</div>}
            </div>
            {action}
        </div>
        {children}
    </Card>
);

const Tile = ({ label, value, hint, tone }: { label: string; value: React.ReactNode; hint?: React.ReactNode; tone?: "warning" | "danger" }) => (
    <div style={{ background: DV2.pageBg, borderRadius: 12, padding: "12px 14px", minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: DV2.muted }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2, color: tone === "danger" ? DV2.danger : tone === "warning" ? DV2.warning : undefined }}>{value}</div>
        {hint && <div style={{ fontSize: 12, color: DV2.muted, lineHeight: 1.4, marginTop: 2 }}>{hint}</div>}
    </div>
);

/** Corps de la fiche, inscription finalisée. */
const DetailBody = ({ detail, teachr, actions }: { detail: ClientDetail; teachr: any; actions: React.ReactNode }) => {
    const { text } = useDv2Theme();
    const client = detail.client;
    const stats = detail.stats;
    const urssaf = detail.urssaf;
    const vt = isVerifiedTrainer();
    const feminine = getActivePrestationBrandConfig() === "prestation" ? "e" : "";
    const clientLabel = getActiveClientBrandConfig();
    const firstName = capitalizeFirstLetter(client.firstName);
    const fullName = `${firstName} ${client.lastName ?? ""}`.trim();
    const active = urssaf.isClientValidate;

    const refused = stats.refusedCount + stats.errorCount;
    const month = stats.currentMonth;

    // Ligne de synthèse : « Avance immédiate active depuis le 19 octobre 2026, 7 cours en octobre »
    // (la première date est celle de l'activation du compte d'avance immédiate, pas celle de l'inscription)
    const monthCourses = `${plural(month.coursesCount, vocab.course(), vocab.courses())} en ${monthName(moment())}`;
    const summary = active && urssaf.activationDate
        ? `Avance immédiate active depuis le ${formatDate(urssaf.activationDate, "D MMMM YYYY")}, ${monthCourses}`
        : [
            client.createdAt ? `${capitalizeFirstLetter(clientLabel)} inscrit${feminine} le ${formatDate(client.createdAt, "D MMMM YYYY")}` : null,
            AccountStatusLabel[urssaf.status] ? `avance immédiate ${AccountStatusLabel[urssaf.status].toLowerCase()}` : null,
            monthCourses,
        ].filter(Boolean).join(", ");
    const totalAmount = vt ? stats.amountTtc : stats.amountNet;
    const monthAmount = vt ? (month.amountTtc ?? month.amountNet) : month.amountNet;
    const description = formatUrssafDescription(urssaf.description);
    const deposits = detail.deposits;

    return (
        <div className="flex flex-col gap-5 " style={{ color: text }}>
            {/* En-tête : identité, statut, synthèse, actions */}
            <Card style={{ padding: 22 }}>
                <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <Avatar text={`${client.firstName?.charAt(0) ?? ""}${client.lastName?.charAt(0) ?? ""}`.toUpperCase()} size={56} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="flex flex-wrap items-center gap-2">
                            <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0, lineHeight: 1.2 }}>{fullName}</h1>
                            <Badge tone={active ? "success" : "warning"}>{active ? "Avance immédiate active" : AccountStatusLabel[urssaf.status] ?? urssaf.status}</Badge>
                        </div>
                        <div style={{ fontSize: 14, color: DV2.muted, marginTop: 4 }}>{summary}</div>
                    </div>
                    <div className="shrink-0">{actions}</div>
                </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-5 items-start">
                {/* Colonne gauche : qui est l'élève, où il en est */}
                <div className="flex flex-col gap-5 min-w-0">
                    <SectionCard title="Coordonnées">
                        <div className="flex flex-col gap-2">
                            <KeyValue label="E-mail" value={client.mail ? <TextLink label={client.mail} href={`mailto:${client.mail}`} size={13} /> : "Non renseigné"} />
                            <KeyValue label="Téléphone" value={client.phone ? <TextLink label={formatPhoneNumber(client.phone)} href={`tel:${client.phone}`} size={13} /> : "Non renseigné"} />
                            <KeyValue label="Adresse" value={client.address ?? "Non renseignée"} />
                            {vt && <KeyValue label="Naissance" value={client.birthday ? `${formatDate(client.birthday, "D MMMM YYYY")}${client.age != null ? `, ${client.age} ans` : ""}` : "Non renseignée"} />}
                            
                        </div>
                    </SectionCard>

                    <SectionCard title="Avance immédiate">
                        {active ? (
                            <Notice tone="success">
                                <b>Le compte URSSAF de {firstName} est créé et validé.</b> Vous pouvez déclarer des {vocab.courses()} pour {getActiveClientBrandConfig("", "cet ", "ce ")}.
                                {urssaf.activationDate ? ` Les demandes de paiement partent à l'URSSAF depuis le ${formatDate(urssaf.activationDate, "D MMMM YYYY")}.` : ""}
                            </Notice>
                        ) : (
                            <ClientStatusBanner status={urssaf.status} description={description} context="page" />
                        )}
                        {description && !active && (
                            <div style={{ fontSize: 12, color: DV2.muted }}><b>Retour de l'URSSAF :</b> {description}</div>
                        )}
                        <div className="flex flex-col gap-2">
                            <KeyValue label="Activée le" value={urssaf.activationDate ? formatDate(urssaf.activationDate, "D MMMM YYYY [à] HH[h]mm") : "Non activée"} />
                            <KeyValue label="Régime" value={urssaf.isScoop ? "Coopérative" : "Standard"} />
                            <KeyValue
                                label="Paiement en attente"
                                value={stats.awaitingValidationCount > 0
                                    ? <span style={{ color: DV2.warning }}>{plural(stats.awaitingValidationCount, vocab.course(), vocab.courses())}, prélèvement pas encore validé</span>
                                    : "Aucun"}
                            />
                            <KeyValue label="Rejets URSSAF" value={refused > 0 ? <span style={{ color: DV2.danger }}>{plural(refused, vocab.course(), vocab.courses())} à corriger dans Mes {vocab.courses()}</span> : "Aucun"} />
                            {(stats.travelReviewCount > 0 || stats.travelRejectedCount > 0) && (
                                <KeyValue label="Déplacement" value={<span style={{ color: stats.travelRejectedCount > 0 ? DV2.danger : DV2.warning }}>{stats.travelRejectedCount > 0 ? `${plural(stats.travelRejectedCount, vocab.course(), vocab.courses())} rejeté${feminine}${stats.travelRejectedCount > 1 ? "s" : ""} par notre équipe` : `${plural(stats.travelReviewCount, vocab.course(), vocab.courses())} en attente de notre équipe`}</span>} />
                            )}
                        </div>
                    </SectionCard>

                    {/* Déplacement à la place de l'ancien bloc Tarifs, section existante dans le style de la fiche */}
                    <ClientTravelSection
                        teachr={teachr}
                        teachrId={teachr.id!}
                        clientId={client.id}
                        clientName={fullName}
                        travelMode={detail.travel.travelMode}
                        coveredBySubscriptionCount={stats.coveredBySubscriptionCount}
                    />
                </div>

                {/* Colonne droite : ce qui s'est passé, ce qui arrive */}
                <div className="flex flex-col gap-5 min-w-0">
                    <SectionCard title="Activité avec vous" subtitle={`Vos ${vocab.courses()} avec ${firstName}`}>
                        <div className="grid grid-cols-2 gap-3">
                            <Tile label="Ce mois-ci" value={plural(month.coursesCount, vocab.course(), vocab.courses())} hint={`${plural(month.sessionsCount ?? month.coursesCount, "séance")}${stats.upcomingCount > 0 ? `, ${stats.upcomingCount} à venir` : ""}`} />
                            <Tile label={vt ? "Prix total" : "Vous avez reçu"} value={euros(totalAmount)} hint={`depuis le début, dont ${euros(monthAmount)} ce mois`} />
                            <Tile label="Heures données" value={hoursFromHalfHours(stats.totalDuration)} hint={`ce mois, ${hoursFromHalfHours(month.duration)}`} />
                            <Tile label={`${feminine ? "Dernière" : "Dernier"} ${vocab.course()}`} value={stats.lastCourseAt ? formatDate(stats.lastCourseAt, "D MMM") : "Aucun"} hint={stats.nextCourseAt ? `prochain${feminine} le ${formatDate(stats.nextCourseAt, "D MMMM")}` : stats.firstCourseAt ? `depuis ${monthName(stats.firstCourseAt)} ${moment(stats.firstCourseAt).format("YYYY")}` : undefined} />
                        </div>
                    </SectionCard>

                    <ClientMonthSessionsSection teachrId={teachr.id!} clientId={client.id} />

                    {isDepositEnabled() && (
                        <SectionCard
                            title="Acomptes"
                            subtitle={deposits && deposits.validCount > 0
                                ? `Attestés sur l'honneur, ${plural(deposits.validCount, "attestation")} en vigueur soit ${euros(deposits.validTotal)} reçus`
                                : `Aucun acompte attesté pour ${getActiveClientBrandConfig("", "cet ", "ce ")}`}
                        >
                            {deposits && deposits.attestations.length > 0 && (
                                <div className="flex flex-col">
                                    {deposits.attestations.map((attestation) => (
                                        <div key={attestation.id} className="flex items-center gap-3" style={{ padding: "10px 0", borderTop: `1px solid ${DV2.borderSoft}` }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontWeight: 800, fontSize: 14, color: attestation.isValid ? undefined : DV2.placeholder, textDecoration: attestation.isValid ? "none" : "line-through" }}>
                                                    {euros(attestation.depositAmount)} versés le {formatDate(attestation.depositDate, "D MMMM YYYY")}
                                                </div>
                                                <div style={{ fontSize: 12, color: DV2.muted }}>
                                                    {attestation.courseLabel}, signée le {formatDate(attestation.signedAt, "D MMMM")}
                                                    {!attestation.isValid ? ` (retirée${attestation.invalidationReason ? ` : ${attestation.invalidationReason}` : ""})` : ""}
                                                </div>
                                            </div>
                                            {!attestation.isValid && <Badge tone="muted">Retiré</Badge>}
                                            <TextLink label="PDF" href={attestation.url} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </SectionCard>
                    )}
                </div>
            </div>

            {/* Documents, pleine largeur */}
            <SectionCard title="Documents" subtitle={`Devis, factures, attestations et abonnements liés à ${firstName}`}>
                <DocumentSpace variant="teachr" ownerId={teachr.id!} clientId={client.id} embedded />
            </SectionCard>
        </div>
    );
};

const ClientDetailV2Page = () => {
    const teachr = useSelector((state: any) => state?.user?.currentUser?.teachr);
    const { clientId: clientIdParam } = useParams();
    const clientId = Number(clientIdParam);
    const navigate = useNavigate();
    const location = useLocation();
    const { primary, fontFamily, text } = useDv2Theme();
    const { detail, row, loading, error, refresh, retry } = useClientDetail(teachr?.id, clientId);
    const [actionLoad, setActionLoad] = useState(false);

    const listLabel = `Mes ${getActiveClientBrandConfig("s")}`;
    const isArchived = detail?.fees?.isArchive ?? false;
    const fullName = detail ? `${capitalizeFirstLetter(detail.client.firstName)} ${detail.client.lastName ?? ""}`.trim() : null;

    const goBack = () => {
        const state = location.state as ClientDetailLocationState | null;
        if (state?.fromList) {
            navigate(-1);
            return;
        }
        navigate(getClientListPath(isArchived));
    };

    const actions = row && teachr ? (
        <ClientRowActions
            client={row}
            teachr={teachr}
            dataDispatch={null}
            load={actionLoad}
            setLoad={setActionLoad}
            variant="page"
            onUpdated={refresh}
            onDeleted={() => navigate(getClientListPath(isArchived), { replace: true })}
        />
    ) : null;

    const body = () => {
        if (loading) {
            return (
                <div style={{ padding: 48, textAlign: "center", color: DV2.muted, fontSize: 14 }} aria-busy="true">
                    <CircularProgress size={18} style={{ marginRight: 8, verticalAlign: "middle" }} /> Chargement de la fiche…
                </div>
            );
        }
        if (error || !detail) {
            return (
                <Notice tone={error === "failed" ? "danger" : "info"} action={error === "failed" ? <TextLink label="Réessayer" onClick={retry} /> : <TextLink label={`Retour à ${listLabel.toLowerCase()}`} onClick={() => navigate(getClientListPath())} />}>
                    {error === "failed"
                        ? "Impossible de charger cette fiche pour le moment. Vérifiez votre connexion puis réessayez."
                        : `Cette fiche est introuvable ou ${getActiveClientBrandConfig("", "cet ", "ce ")} n'est pas rattaché à votre compte.`}
                </Notice>
            );
        }
        if (!isRegistrationFinalized(detail.urssaf.status)) {
            // Inscription non finalisée : identité, statut et actions seulement, comme la fiche actuelle.
            return (
                <div className="flex flex-col gap-5 max-w-2xl" style={{ color: text }}>
                    <Card style={{ padding: 22 }}>
                        <div className="flex flex-col md:flex-row md:items-start gap-4">
                            <Avatar text={`${detail.client.firstName?.charAt(0) ?? ""}${detail.client.lastName?.charAt(0) ?? ""}`.toUpperCase()} size={56} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0 }}>{fullName}</h1>
                                <div style={{ fontSize: 14, color: DV2.muted, marginTop: 4 }}>Inscription en cours</div>
                            </div>
                            <div className="shrink-0">{actions}</div>
                        </div>
                        <div style={{ marginTop: 16 }}>
                            <ClientStatusBanner status={detail.urssaf.status} description={formatUrssafDescription(detail.urssaf.description)} context="page" />
                        </div>
                    </Card>
                    <Notice tone="info">
                        La fiche complète (activité, tarifs, déplacements et documents) sera disponible dès que l'inscription {getActiveClientBrandConfig("", "de l'", "du ")} sera finalisée.
                    </Notice>
                </div>
            );
        }
        return (
            // Déplacement et Séances du mois (sections existantes) prennent le style des autres blocs de la fiche
            <DetailSectionStyleContext.Provider value="v2">
                <DetailBody detail={detail} teachr={teachr} actions={actions} />
            </DetailSectionStyleContext.Provider>
        );
    };

    return (
        <IndexApp
            navBar={{ index: 2 }}
            teachr={teachr}
            component={
                <main style={{ background: DV2.pageBg, minHeight: "calc(100vh - 64px)", fontFamily, color: text }}>
                    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 py-4 lg:py-8 flex flex-col gap-5">
                        <nav aria-label="Fil d'Ariane" className="flex items-center gap-2" style={{ fontSize: 14 }}>
                            <button type="button" onClick={goBack} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: primary, fontWeight: 800, fontFamily }}>‹ {listLabel}</button>
                            {fullName && <><span style={{ color: DV2.placeholder }}>/</span><span style={{ color: DV2.muted }}>{fullName}</span></>}
                        </nav>
                        {teachr?.id ? body() : null}
                    </div>
                </main>
            }
        />
    );
};

export default ClientDetailV2Page;
